import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { liveQuery, type Subscription } from "dexie";

import { Maybe } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import type { AudioRecordingObservable } from "@/store/audio-recordings";
import type { AppStoreAudioRecordingStore } from "@/store/audio-recordings/audio-recording.store";

type ConstructorArgs = {
  store: AppStoreAudioRecordingStore;
  noteId: Uuid | null;
};

export class NoteAudioRecordingObservable {
  private store: AppStoreAudioRecordingStore;
  private noteId: Uuid | null;
  private subscription?: Subscription;

  recordingId: Maybe<Uuid>;
  isLoading: boolean;

  constructor({ store, noteId }: ConstructorArgs) {
    this.store = store;
    this.noteId = noteId;
    this.recordingId = undefined;
    this.isLoading = true;

    makeObservable<this, "store" | "noteId" | "subscription">(this, {
      store: false,
      noteId: observable,
      subscription: observable,
      recordingId: observable,
      isLoading: observable,
      recording: computed,
      subscribe: action,
      unsubscribe: action,
      dispose: action,
    });

    onBecomeObserved(this, "recording", () => this.subscribe());
    onBecomeUnobserved(this, "recording", () => this.unsubscribe());
  }

  get recording(): Maybe<AudioRecordingObservable> {
    return this.recordingId ? this.store.get(this.recordingId) : undefined;
  }

  subscribe() {
    this.unsubscribe();

    runInAction(() => {
      this.isLoading = true;
    });

    const targetNoteId = this.noteId;

    if (!targetNoteId) {
      return;
    }

    this.subscription = liveQuery(async () => {
      const clientGroupId = await this.store.getLocalClientGroupId();

      const latestRecording = await this.store.localTable
        .where("associated_note_id")
        .equals(targetNoteId)
        .filter(row => row.trashed_at === null)
        .filter(row => row.associated_client_id === clientGroupId)
        .sortBy("locally_created_at");

      return latestRecording.at(-1);
    }).subscribe({
      next: record => {
        runInAction(() => {
          this.recordingId = record?.model_id;
          this.isLoading = false;
        });
      },
      error: () => {
        runInAction(() => {
          this.recordingId = undefined;
          this.isLoading = false;
        });
      },
    });
  }

  unsubscribe() {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  dispose() {
    this.unsubscribe();
  }
}
